import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ChevronRightIcon, PaperclipIcon } from '../theme/Icons';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ICaseloadSearchRow } from './ICaseloadSearch';

const useStyles = createUseStyles({
    row: {
        padding: '10px 4px',
        borderTop: '1px solid var(--ion-color-step-100)',

        '&:first-child': {
            borderTop: 'none'
        }
    },

    // The whole row opens the client, so it is one button rather than a clickable container. The
    // attachment below sits OUTSIDE it: a link nested in a button is invalid, and role="button" on a
    // container is children-presentational, which strips the link's own role from assistive tech.
    open: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        width: '100%',
        padding: 0,
        border: 'none',
        background: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer'
    },

    unavailable: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1.45,
        color: 'var(--ion-color-step-600)',
        marginTop: 2,
        fontStyle: 'italic' as const
    },

    body: {
        flex: 1,
        minWidth: 0
    },

    name: {
        display: 'block',
        fontSize: 15,
        fontWeight: 500,
        color: 'var(--ion-color-primary)'
    },

    reason: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1.45,
        color: 'var(--ion-color-step-650)',
        marginTop: 2
    },

    document: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 6,
        fontSize: 13,
        color: 'var(--ion-color-primary)',
        textDecoration: 'none'
    },

    chevron: {
        flexShrink: 0,
        paddingTop: 2
    }
});

/**
 * One client in an assembled caseload answer. The whole row opens that client, because a name in a
 * list of people who need something is only useful if it is the way in to them.
 */
export const CaseloadClientRow: FC<{ row: ICaseloadSearchRow }> = ({ row }) => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);

    const open = () => {
        history.push(`/care/${row.client.careMapKey.careMapId}`);
    };

    return (
        <div
            className={classes.row}
            data-type="caseload-client-row"
            data-client-caremap-id={row.client.careMapKey.careMapId}
        >
            <button type="button" className={classes.open} onClick={open}>
                <span className={classes.body}>
                    {/* Client names are PHI-adjacent, so they stay out of session replay. */}
                    <span className={`${classes.name} mp-mask`}>{row.client.name}</span>
                    {row.answer && <span className={`${classes.reason} mp-mask`}>{row.answer}</span>}

                    {/* A name with no reason under it reads as an empty answer rather than a client
                        who could not be read, and the operator cannot tell who to ask about again. */}
                    {row.status === 'unavailable' && row.failureMessage && (
                        <span className={classes.unavailable}>{row.failureMessage}</span>
                    )}
                </span>
                <span className={classes.chevron}>
                    <ChevronRightIcon color="medium" size="small" />
                </span>
            </button>

            {/* The document itself, when the answer came from one and that file was found on the
                client's record. Opening it is usually the reason the question was asked. */}
            {row.source && (
                <a
                    className={`${classes.document} mp-mask`}
                    href={row.source.url}
                    target="_blank"
                    rel="noreferrer"
                    data-type="caseload-source-document"
                >
                    <PaperclipIcon color="primary" size="small" />
                    {row.source.fileName}
                </a>
            )}
        </div>
    );
};
